import type { HTMLAttributes } from 'react';

import { useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { getTestProps } from '../../lib/helpers';

import useDarkMode from '../../hooks/useDarkMode';

import {
  CheckCircleIcon,
  DotCrossedIcon,
  DotCrossedIconDarkMode,
  DotIcon,
} from '../../images/shapes';

interface CheckCircleProps extends HTMLAttributes<SVGElement> {
  /**
   * True of false checkbox
   */
  checked?: boolean;
  /**
   * By default, false checkbox has crossed icon.
   * Set this to true, to remove the icon
   */
  emptyFalseState?: boolean;
  /**
   * Additional CSS classes for component
   */
  additionalClasses?: string;
  /**
   * Component test id
   */
  testId?: string;
}

const CheckCircle = ({
  checked = true,
  emptyFalseState = false,
  additionalClasses = '',
  testId,
  ...props
}: CheckCircleProps) => {
  const [darkMode] = useDarkMode();

  const CrossedIconComponent = useMemo(() => {
    if (emptyFalseState) return DotIcon;
    return darkMode ? DotCrossedIconDarkMode : DotCrossedIcon;
  }, [darkMode, emptyFalseState]);

  return (
    <>
      {checked ? (
        <CheckCircleIcon
          className={twMerge('text-blue w-6', additionalClasses)}
          {...props}
          {...getTestProps(testId, 'checked')}
        />
      ) : (
        <CrossedIconComponent
          className={twMerge(
            'text-slate-100 dark:text-slate-700 w-6',
            additionalClasses,
          )}
          {...props}
          {...getTestProps(testId, 'unchecked')}
        />
      )}
    </>
  );
};

export default CheckCircle;
